/**
 * SOS history — list every SOS the family has ever received, with
 * ack + resolve actions. Active (un-acked) events surface first; the
 * card-row visual state matches the workflow: red → amber → grey.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, MapPin, Siren, MessageSquare } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { usersApi } from '@/api/users';
import { locationsApi, type SosEvent } from '@/api/locations';
import { PageHeader } from './PageHeader';

type Status = 'active' | 'acked' | 'resolved';

function statusOf(e: SosEvent): Status {
  if (e.resolved_at) return 'resolved';
  if (e.acknowledged_at) return 'acked';
  return 'active';
}

export function SosPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const sos = useQuery({
    queryKey: ['sos'],
    queryFn: () => locationsApi.listSos(),
    refetchInterval: 15_000,
  });
  const [resolveFor, setResolveFor] = useState<SosEvent | null>(null);

  const childMap = new Map((children.data ?? []).map((c) => [c.id, c]));

  const ack = useMutation({
    mutationFn: (sosId: string) => locationsApi.ackSos(sosId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sos'] }),
    onError: () => toast.error('Could not acknowledge.'),
  });

  const resolve = useMutation({
    mutationFn: ({ sosId, note }: { sosId: string; note?: string }) =>
      locationsApi.resolveSos(sosId, note),
    onSuccess: () => {
      toast.success('SOS resolved.');
      setResolveFor(null);
      void queryClient.invalidateQueries({ queryKey: ['sos'] });
    },
    onError: () => toast.error('Could not resolve.'),
  });

  const active = (sos.data ?? []).filter((e) => statusOf(e) !== 'resolved');
  const archived = (sos.data ?? []).filter((e) => statusOf(e) === 'resolved');

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.sos.title')}
        icon={Siren}
        tint="green"
        description={t('pages.sos.description')}
      />

      {sos.isPending && (
        <Card className="mt-6">
          <CardContent className="space-y-3 p-6">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      )}

      {!sos.isPending && (sos.data?.length ?? 0) === 0 && (
        <Card className="mt-6">
          <CardContent className="flex items-center gap-3 p-6 text-sm text-[color:var(--kz-color-ink-secondary)]">
            <Siren className="size-5 text-emerald-600" />
            No SOS events recorded. Keep it that way.
          </CardContent>
        </Card>
      )}

      {active.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
            Open ({active.length})
          </h2>
          <div className="space-y-3">
            {active.map((e) => (
              <SosCard
                key={e.id}
                event={e}
                child={childMap.get(e.child_id)}
                onAck={() => ack.mutate(e.id)}
                onResolve={() => setResolveFor(e)}
                ackPending={ack.isPending}
              />
            ))}
          </div>
        </section>
      )}

      {archived.length > 0 && (
        <section className="mt-8">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-[color:var(--kz-color-ink-muted)]">
            Resolved
          </h2>
          <div className="space-y-3">
            {archived.map((e) => (
              <SosCard
                key={e.id}
                event={e}
                child={childMap.get(e.child_id)}
                onAck={() => {}}
                onResolve={() => {}}
                ackPending={false}
              />
            ))}
          </div>
        </section>
      )}

      <ResolveDialog
        event={resolveFor}
        onClose={() => setResolveFor(null)}
        onResolve={(note) => resolve.mutate({ sosId: resolveFor!.id, note })}
        pending={resolve.isPending}
      />
    </div>
  );
}

function SosCard({
  event,
  child,
  onAck,
  onResolve,
  ackPending,
}: {
  event: SosEvent;
  child?: { name: string; color: string | null };
  onAck: () => void;
  onResolve: () => void;
  ackPending: boolean;
}) {
  const status = statusOf(event);
  const triggered = new Date(event.triggered_at);
  const stateClasses = {
    active: 'border-rose-300 bg-rose-50',
    acked: 'border-amber-300 bg-amber-50',
    resolved: 'border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-card)]',
  }[status];

  return (
    <Card className={`border ${stateClasses}`}>
      <CardContent className="flex flex-wrap items-start gap-4 p-4 md:flex-nowrap">
        <span
          className={
            status === 'active'
              ? 'grid size-10 shrink-0 place-items-center rounded-full bg-rose-600 text-white'
              : status === 'acked'
                ? 'grid size-10 shrink-0 place-items-center rounded-full bg-amber-500 text-white'
                : 'grid size-10 shrink-0 place-items-center rounded-full bg-emerald-600 text-white'
          }
        >
          {status === 'resolved' ? <CheckCircle2 className="size-5" /> : <Siren className="size-5" />}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">
            {child?.name ?? 'Unknown child'} — {status}
          </p>
          <p className="text-xs text-[color:var(--kz-color-ink-secondary)]">
            Triggered {triggered.toLocaleString()}
          </p>
          {event.lat != null && event.lng != null && (
            <Link
              // Historical coords — pass lat/lng, not the child id. Otherwise
              // the map would fly to the child's *current* position, which is
              // almost never where the SOS actually fired.
              to={`/map?lat=${event.lat}&lng=${event.lng}&focus=${event.child_id}`}
              className="mt-1 inline-flex items-center gap-1 text-xs text-[color:var(--kz-color-brand-primary)] underline-offset-2 hover:underline"
            >
              <MapPin className="size-3" />
              {event.lat.toFixed(4)}, {event.lng.toFixed(4)}
            </Link>
          )}
          {event.acknowledged_at && (
            <p className="mt-1 text-xs text-[color:var(--kz-color-ink-muted)]">
              Acknowledged {new Date(event.acknowledged_at).toLocaleString()}
            </p>
          )}
          {event.resolved_at && (
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
              Resolved {new Date(event.resolved_at).toLocaleString()}
              {event.resolution_note && ` · ${event.resolution_note}`}
            </p>
          )}
        </div>
        {status !== 'resolved' && (
          <div className="flex shrink-0 gap-2">
            {status === 'active' && (
              <Button variant="outline" size="sm" onClick={onAck} disabled={ackPending}>
                Acknowledge
              </Button>
            )}
            <Button size="sm" onClick={onResolve}>
              Resolve
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ResolveDialog({
  event,
  onClose,
  onResolve,
  pending,
}: {
  event: SosEvent | null;
  onClose: () => void;
  onResolve: (note?: string) => void;
  pending: boolean;
}) {
  const [note, setNote] = useState('');
  if (!event) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Resolve SOS</DialogTitle>
          <DialogDescription>
            Add an optional note describing what happened.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="note" className="flex items-center gap-1.5 text-sm">
            <MessageSquare className="size-3.5" /> Resolution note
          </Label>
          <Input
            id="note"
            placeholder="e.g. false alarm — kid tested the button"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onResolve(note.trim() || undefined)} disabled={pending}>
            {pending ? 'Resolving…' : 'Mark resolved'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
